/**
 * PHASE 8: SyncStatusPill Component
 * Compact status indicator showing offline sync state
 * 
 * States:
 * - Online (green): All synced
 * - Pending (blue): Items waiting to sync
 * - Syncing (blue + spinner): Currently syncing
 * - Offline (amber): Device is offline
 * - Error (red): Failed items need attention
 * - Conflict (purple): Conflicts need resolution
 */

import { useState, useEffect } from 'react';
import { 
  Cloud, 
  CloudOff, 
  RefreshCw, 
  AlertCircle, 
  CheckCircle,
  AlertTriangle,
  ChevronRight
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useOfflineSync } from '@/hooks/useOfflineSync';

// Status configuration
const STATUS_CONFIG = {
  online: {
    icon: CheckCircle,
    bg: 'bg-green-100 dark:bg-green-900/30',
    text: 'text-green-700 dark:text-green-400',
    border: 'border-green-200 dark:border-green-800',
    label: 'Synced'
  },
  pending: {
    icon: Cloud,
    bg: 'bg-blue-100 dark:bg-blue-900/30',
    text: 'text-blue-700 dark:text-blue-400',
    border: 'border-blue-200 dark:border-blue-800',
    label: 'Pending'
  },
  syncing: {
    icon: RefreshCw,
    bg: 'bg-blue-100 dark:bg-blue-900/30',
    text: 'text-blue-700 dark:text-blue-400',
    border: 'border-blue-200 dark:border-blue-800',
    label: 'Syncing',
    animate: true
  },
  offline: {
    icon: CloudOff,
    bg: 'bg-amber-100 dark:bg-amber-900/30',
    text: 'text-amber-700 dark:text-amber-400',
    border: 'border-amber-200 dark:border-amber-800',
    label: 'Offline'
  },
  error: {
    icon: AlertCircle,
    bg: 'bg-red-100 dark:bg-red-900/30',
    text: 'text-red-700 dark:text-red-400',
    border: 'border-red-200 dark:border-red-800',
    label: 'Failed'
  },
  conflict: {
    icon: AlertTriangle,
    bg: 'bg-purple-100 dark:bg-purple-900/30',
    text: 'text-purple-700 dark:text-purple-400',
    border: 'border-purple-200 dark:border-purple-800',
    label: 'Conflict'
  }
};

/**
 * Get status from sync state
 */
function getStatusFromState({ isOnline, isSyncing, pendingCount, failedCount, conflictCount }) {
  if (!isOnline) return 'offline';
  if (isSyncing) return 'syncing';
  if (conflictCount > 0) return 'conflict';
  if (failedCount > 0) return 'error';
  if (pendingCount > 0) return 'pending';
  return 'online';
}

/**
 * SyncStatusPill - Compact version
 */
export function SyncStatusPill({ 
  onClick, 
  className,
  showLabel = true,
  showCount = true,
  size = 'md' // 'sm', 'md', 'lg'
}) {
  const {
    isOnline,
    isSyncing,
    pendingCount,
    failedCount,
    conflictCount,
    syncNow
  } = useOfflineSync();

  const status = getStatusFromState({ isOnline, isSyncing, pendingCount, failedCount, conflictCount });
  const config = STATUS_CONFIG[status];
  const Icon = config.icon;
  
  const totalIssues = failedCount + conflictCount;
  const totalPending = pendingCount + totalIssues;

  const sizeClasses = {
    sm: 'h-6 px-2 text-xs gap-1',
    md: 'h-8 px-3 text-sm gap-1.5',
    lg: 'h-10 px-4 text-base gap-2'
  };

  const iconSizes = {
    sm: 'h-3 w-3',
    md: 'h-4 w-4',
    lg: 'h-5 w-5'
  };

  const handleClick = (e) => {
    if (onClick) {
      onClick(e);
    }
  };

  return (
    <button
      onClick={handleClick}
      className={cn(
        "inline-flex items-center rounded-full border transition-all duration-200",
        "hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2",
        config.bg,
        config.text,
        config.border,
        `focus:ring-${config.text.split('-')[1]}-500`,
        sizeClasses[size],
        className
      )}
      aria-label={`Sync status: ${config.label}${totalPending > 0 ? ` (${totalPending} items)` : ''}`}
    >
      <Icon 
        className={cn(
          iconSizes[size],
          config.animate && 'animate-spin'
        )} 
      />
      
      {showLabel && (
        <span className="font-medium">
          {config.label}
        </span>
      )}
      
      {showCount && totalPending > 0 && status !== 'online' && (
        <span className={cn(
          "inline-flex items-center justify-center rounded-full font-semibold",
          "bg-current/20",
          size === 'sm' ? 'h-4 min-w-4 px-1 text-[10px]' : 
          size === 'md' ? 'h-5 min-w-5 px-1 text-xs' : 
          'h-6 min-w-6 px-1.5 text-sm'
        )}>
          {totalPending}
        </span>
      )}

      {onClick && (
        <ChevronRight className={cn(iconSizes[size], 'opacity-60')} />
      )}
    </button>
  );
}

/**
 * SyncStatusDot - Minimal dot indicator
 */
export function SyncStatusDot({ className, showPulse = true }) {
  const {
    isOnline,
    isSyncing,
    pendingCount,
    failedCount,
    conflictCount
  } = useOfflineSync();

  const status = getStatusFromState({ isOnline, isSyncing, pendingCount, failedCount, conflictCount });
  
  const dotColors = {
    online: 'bg-green-500',
    pending: 'bg-blue-500',
    syncing: 'bg-blue-500',
    offline: 'bg-amber-500',
    error: 'bg-red-500',
    conflict: 'bg-purple-500'
  };

  const shouldPulse = showPulse && (status === 'syncing' || status === 'pending');

  return (
    <span className={cn("relative inline-flex", className)}>
      <span 
        className={cn(
          "h-2.5 w-2.5 rounded-full",
          dotColors[status]
        )} 
      />
      {shouldPulse && (
        <span 
          className={cn(
            "absolute inline-flex h-full w-full rounded-full opacity-75 animate-ping",
            dotColors[status]
          )} 
        />
      )}
    </span>
  );
}

/**
 * SyncStatusBadge - Badge variant with more detail
 */
export function SyncStatusBadge({ 
  className,
  onClick,
  variant = 'default' // 'default', 'minimal', 'detailed'
}) {
  const {
    isOnline,
    isSyncing,
    pendingCount,
    failedCount,
    conflictCount,
    lastSyncedAt
  } = useOfflineSync();

  const status = getStatusFromState({ isOnline, isSyncing, pendingCount, failedCount, conflictCount });
  const config = STATUS_CONFIG[status];
  const Icon = config.icon;

  // Format last synced time
  const formatLastSync = (timestamp) => {
    if (!timestamp) return 'Never';
    const diff = Date.now() - timestamp;
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return new Date(timestamp).toLocaleDateString();
  };

  if (variant === 'minimal') {
    return (
      <div 
        className={cn(
          "inline-flex items-center gap-1.5 text-xs",
          config.text,
          className
        )}
        onClick={onClick}
        role={onClick ? 'button' : undefined}
      >
        <Icon className={cn("h-3 w-3", config.animate && 'animate-spin')} />
        <span>{config.label}</span>
      </div>
    );
  }

  if (variant === 'detailed') {
    return (
      <div 
        className={cn(
          "flex items-center gap-3 p-3 rounded-lg border",
          config.bg,
          config.border,
          onClick && 'cursor-pointer hover:shadow-sm transition-shadow',
          className
        )}
        onClick={onClick}
        role={onClick ? 'button' : undefined}
      >
        <div className={cn(
          "flex items-center justify-center h-10 w-10 rounded-full",
          "bg-current/10"
        )}>
          <Icon className={cn("h-5 w-5", config.text, config.animate && 'animate-spin')} />
        </div>
        
        <div className="flex-1 min-w-0">
          <p className={cn("font-medium", config.text)}>
            {config.label}
            {pendingCount > 0 && status !== 'online' && (
              <span className="ml-1">({pendingCount})</span>
            )}
          </p>
          <p className="text-xs text-muted-foreground truncate">
            {status === 'offline' 
              ? 'Changes saved locally'
              : status === 'syncing'
              ? 'Syncing changes...'
              : status === 'error'
              ? `${failedCount} failed item${failedCount !== 1 ? 's' : ''}`
              : status === 'conflict'
              ? `${conflictCount} conflict${conflictCount !== 1 ? 's' : ''} to resolve`
              : `Last synced: ${formatLastSync(lastSyncedAt)}`
            }
          </p>
        </div>
        
        {onClick && (
          <ChevronRight className="h-5 w-5 text-muted-foreground" />
        )}
      </div>
    );
  }

  // Default variant
  return (
    <div 
      className={cn(
        "inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm",
        config.bg,
        config.text,
        config.border,
        onClick && 'cursor-pointer hover:shadow-sm transition-shadow',
        className
      )}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
    >
      <Icon className={cn("h-4 w-4", config.animate && 'animate-spin')} />
      <span className="font-medium">{config.label}</span>
      {pendingCount > 0 && status !== 'online' && (
        <span className="text-xs opacity-75">({pendingCount})</span>
      )}
    </div>
  );
}

export default SyncStatusPill;
